import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, PlusCircle, MessageSquare, User, Crown, Shield, LogOut, Bell, Gift, Facebook, Map } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const navItems = [
  { to: "/app", label: "Esplora", icon: Home, testid: "nav-feed" },
  { to: "/new", label: "Dona", icon: PlusCircle, testid: "nav-new" },
  { to: "/requests", label: "Richieste", icon: MessageSquare, testid: "nav-requests" },
  { to: "/profile", label: "Profilo", icon: User, testid: "nav-profile" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);

  const loadNotifs = async () => {
    try { const { data } = await api.get("/notifications"); setNotifs(data); } catch {}
  };
  useEffect(() => { loadNotifs(); const i = setInterval(loadNotifs, 20000); return () => clearInterval(i); }, []);
  const unread = notifs.filter((n) => !n.read).length;

  const initials = (user?.name || "U").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen pb-28 md:pb-0">
      <header className="sticky top-0 z-40 glass border-b border-border/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/app" className="flex items-center gap-2" data-testid="logo-link">
            <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
              <Gift className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-head font-extrabold text-xl tracking-tight text-primary">Ti Regalo</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((it) => (
              <NavLink key={it.to} to={it.to} data-testid={`${it.testid}-desktop`}
                className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"}`}>
                {it.label}
              </NavLink>
            ))}
            <NavLink to="/mappa" data-testid="nav-map-desktop" className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"}`}><Map className="h-4 w-4" /> Mappa</NavLink>
            {user?.role === "admin" && (
              <NavLink to="/admin" data-testid="nav-admin-desktop"
                className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"}`}>
                <Shield className="h-4 w-4" /> Admin
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/premium" data-testid="credits-badge"
              className="flex items-center gap-1.5 rounded-full bg-[hsl(var(--gold))]/15 border border-[hsl(var(--gold))]/40 px-3 py-1.5 text-sm font-semibold text-[hsl(30_60%_35%)] hover:bg-[hsl(var(--gold))]/25 transition-colors">
              <Crown className="h-4 w-4 text-[hsl(var(--gold))]" />
              {user?.credits ?? 0}
            </Link>

            <DropdownMenu onOpenChange={(o) => { if (o && unread) api.post("/notifications/read").then(loadNotifs); }}>
              <DropdownMenuTrigger data-testid="notif-trigger" className="relative p-2 rounded-full hover:bg-secondary transition-colors">
                <Bell className="h-5 w-5" />
                {unread > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-accent text-[10px] text-white flex items-center justify-center">{unread}</span>}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-auto">
                <DropdownMenuLabel>Notifiche</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifs.length === 0 && <div className="px-3 py-6 text-sm text-muted-foreground text-center">Nessuna notifica</div>}
                {notifs.map((n) => (
                  <div key={n.id} className="px-3 py-2 text-sm border-b border-border/40 last:border-0" data-testid="notif-item">{n.text}</div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger data-testid="avatar-trigger">
                <Avatar className="h-9 w-9 ring-2 ring-primary/20">
                  <AvatarImage src={mediaUrl(user?.avatar_url)} />
                  <AvatarFallback className="bg-primary/10 text-primary font-semibold">{initials}</AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex flex-col">
                  <span className="font-semibold">{user?.name}</span>
                  <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><User className="h-4 w-4 mr-2" /> Profilo</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/premium")} data-testid="menu-premium"><Crown className="h-4 w-4 mr-2" /> Premium & Crediti</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/import")} data-testid="menu-import"><Facebook className="h-4 w-4 mr-2" /> Importa da Facebook</DropdownMenuItem>
                {user?.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Shield className="h-4 w-4 mr-2" /> Admin</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-destructive"><LogOut className="h-4 w-4 mr-2" /> Esci</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Outlet />
      </main>

      <nav className="md:hidden fixed bottom-4 left-4 right-4 z-40 glass rounded-full tactile-shadow px-2 py-2 flex items-center justify-around">
        {navItems.map((it) => (
          <NavLink key={it.to} to={it.to} data-testid={`${it.testid}-mobile`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-full transition-colors ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            <it.icon className="h-5 w-5" />
            <span className="text-[10px] font-medium">{it.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
